import type { EditorView } from 'prosemirror-view';
import type { WorkspaceQuestion } from '../types';
import { buildCommentToQuestionMap } from './commentCorrelation';

/**
 * Position extraction for margin-aligned Q&A rows.
 *
 * Given the live ProseMirror view, the editor's scroll container, and the
 * reviewable questions, compute a `Map<questionId, number>` of viewport-relative
 * top px — each answered question's vertical offset from the top of the editor
 * pane, so its Q&A row can be absolutely positioned next to its answer
 * (Word/Google-Docs comment-rail style).
 *
 * How it works:
 *  1. Walk the PM doc; for every comment MARK, record the first PM position at
 *     which that comment id appears. Comment ranges are marks, so a comment
 *     spanning several text nodes is recorded once (at its earliest pos).
 *  2. For each comment id, `view.coordsAtPos(pos)` gives viewport coords; we
 *     subtract the editor pane's top to make it pane-relative.
 *  3. Map comment id → question id by the shared order correlation (Nth comment
 *     ↔ Nth answered question), so the caller keys the result by question id.
 *
 * The exact PM mark name/attr is uncertain across editor versions, so step (1)
 * is deliberately defensive: it matches any mark whose type name CONTAINS
 * "comment" (case-insensitive) and reads the id from `commentId` or `id`
 * attrs. It logs (console.debug) how many markers resolved so the strategy can
 * be tuned live in the browser.
 */

/** A mark whose type name contains "comment" (case-insensitive). */
function isCommentMarkName(name: string): boolean {
  return name.toLowerCase().includes('comment');
}

/**
 * Read a numeric comment id off a mark's attrs, trying the known attr names in
 * order. Returns null when none resolve to a finite number.
 */
function readCommentId(attrs: Record<string, unknown> | undefined): number | null {
  if (!attrs) return null;
  // The shipped schema uses `commentId`; older/other builds may use `id`.
  const candidates = [attrs.commentId, attrs.id];
  for (const raw of candidates) {
    if (raw == null) continue;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Walk the document and record the first PM position of each comment marker.
 * Returns a map of commentId → earliest PM pos.
 *
 * Exported so it can be unit-tested with a hand-built doc/view without a real
 * browser.
 */
export function collectCommentMarkPositions(view: EditorView): Map<number, number> {
  const firstPos = new Map<number, number>();
  view.state.doc.descendants((node, pos) => {
    // Marks live on inline (text) nodes; record the earliest pos per id.
    for (const mark of node.marks) {
      if (!isCommentMarkName(mark.type.name)) continue;
      const id = readCommentId(mark.attrs as Record<string, unknown>);
      if (id == null) continue;
      if (!firstPos.has(id)) {
        firstPos.set(id, pos);
      }
    }
    return true;
  });
  return firstPos;
}

/**
 * Reverse lookup for selection-sync (editor → panel): given a PM position,
 * return the comment id of the comment mark covering that position, or null.
 *
 * This is the inverse of `collectCommentMarkPositions`: instead of scanning the
 * whole doc for every marker, it reads only the marks at (and immediately
 * around) a single position — the spot the reviewer just clicked. We match the
 * SAME mark family used everywhere else (type name contains "comment", id from
 * `commentId` then `id`).
 *
 * It is deliberately defensive across PM's resolve edge cases:
 *  - `$pos.marks()` gives the marks at the boundary, which is empty when the
 *    cursor sits at the very start/end of a marked range;
 *  - so we also consult `nodeBefore` / `nodeAfter` marks, and as a last resort
 *    re-resolve one position back. Any throw resolves to null (never crashes a
 *    click handler). Exported for unit testing with a hand-built resolve.
 */
export function commentIdAtPos(view: EditorView, pos: number): number | null {
  try {
    const { doc } = view.state;
    const clamped = Math.max(0, Math.min(pos, doc.content.size));
    const $pos = doc.resolve(clamped);

    // Gather candidate mark sets from the boundary and the adjacent nodes — a
    // click can land at either edge of a comment range, where `$pos.marks()`
    // alone misses the mark.
    const markSets = [
      $pos.marks(),
      $pos.nodeBefore?.marks,
      $pos.nodeAfter?.marks,
    ];
    for (const marks of markSets) {
      if (!marks) continue;
      for (const mark of marks) {
        if (!isCommentMarkName(mark.type.name)) continue;
        const id = readCommentId(mark.attrs as Record<string, unknown>);
        if (id != null) return id;
      }
    }
    return null;
  } catch {
    // Resolve can throw for an out-of-range pos or a torn-down view → no match.
    return null;
  }
}

export interface ComputeRowPositionsArgs {
  /** The live ProseMirror view (from `editorRef.getView()`). */
  view: EditorView;
  /** The editor scroll container's bounding rect (positions are relative to its top). */
  editorPaneRect: { top: number };
  /** The reviewable questions, used to map comment id → question id by order. */
  questions: WorkspaceQuestion[];
}

/**
 * Compute the `Map<questionId, viewport-relative top px>` for every answered
 * question that has a resolvable comment marker. Questions whose comment can't
 * be located (no marker, no coords) are simply absent from the map — the panel
 * treats them as "no position" and keeps them out of the aligned rail.
 *
 * Logs (console.debug) how many markers matched vs. how many mapped to a
 * question, so the mark-name/attr strategy can be tuned against a real doc.
 */
export function computeRowPositions({
  view,
  editorPaneRect,
  questions,
}: ComputeRowPositionsArgs): Map<string, number> {
  const result = new Map<string, number>();

  const commentPositions = collectCommentMarkPositions(view);
  const commentToQuestion = buildCommentToQuestionMap(questions, [...commentPositions.keys()].map(
    // buildCommentToQuestionMap correlates by ORDER (sorted comment id ↔ sorted
    // answered question), so it only needs each comment's id + a null parentId.
    // Synthesize minimal Comment shapes from the resolved ids; the map ignores
    // every other field.
    (id) => ({ id, parentId: null, author: '', content: [] }),
  ));

  let placed = 0;
  for (const [commentId, pmPos] of commentPositions) {
    const questionId = commentToQuestion.get(commentId);
    if (!questionId) continue;
    let topPx: number;
    try {
      const rect = view.coordsAtPos(pmPos);
      topPx = rect.top - editorPaneRect.top;
    } catch {
      // coordsAtPos can throw for a pos that isn't laid out yet — skip it.
      continue;
    }
    result.set(questionId, topPx);
    placed += 1;
  }

  // Tunable observability: surfaces how the defensive mark match performed so
  // the exact mark-name/attr can be confirmed live in the browser console.
  console.debug(
    `[workspace] aligned positions: ${commentPositions.size} comment markers resolved, ${placed} mapped to questions`,
  );

  return result;
}

/** Tunable layout constants for the aligned rail. Adjust to taste in the browser. */
export const ALIGNED_LAYOUT = {
  /** Min vertical gap (px) between two stacked rows before they collide. */
  ROW_GAP: 8,
  /**
   * Estimated row height (px) used for collision stacking when a row hasn't
   * been measured yet. The real measured height (when available) overrides it.
   */
  ESTIMATED_ROW_HEIGHT: 64,
  /**
   * Vertical nudge (px) applied to a row's target top so its top edge lines up
   * a touch above the answer's text baseline. 0 = top-aligned to the marker.
   */
  ROW_TOP_OFFSET: 0,
} as const;

export interface PositionedRow<T> {
  item: T;
  /** The resolved top px after collision stacking. */
  top: number;
}

/**
 * Collision-stack positioned rows: sort by target top; if a row would overlap
 * the previous one (`prevTop + prevHeight + gap > thisTop`), push it down to
 * `prevTop + prevHeight + gap`. Pure and deterministic so it can be unit-tested.
 *
 * @param rows         items with their raw target top (already pane-relative)
 * @param heightOf     measured height for an item, or the estimate fallback
 * @param gap          min vertical gap between stacked rows
 */
export function stackRows<T>(
  rows: Array<{ item: T; top: number }>,
  heightOf: (item: T) => number,
  gap: number = ALIGNED_LAYOUT.ROW_GAP,
): Array<PositionedRow<T>> {
  const sorted = [...rows].sort((a, b) => a.top - b.top);
  const out: Array<PositionedRow<T>> = [];
  let prevBottom = -Infinity;
  for (const row of sorted) {
    const top = Math.max(row.top, prevBottom + gap);
    out.push({ item: row.item, top });
    prevBottom = top + heightOf(row.item);
  }
  return out;
}
